// Public page reached by scanning the counter QR code: /member/register
import React from "react";
import { Button, Card, CardBody, Form, FormGroup, Input, Label, Spinner } from "reactstrap";
import { Helmet } from "react-helmet";
import { MEMBER_API } from "./memberApi.js";
import "./member.css";

const TEXT = {
  en: {
    title: "Become a Member",
    subtitle: "Register for free and enjoy member-only promotions & birthday treats.",
    name: "Full Name (as per IC)",
    phone: "Phone Number",
    icnumber: "IC / Passport Number",
    email: "Email",
    birthday: "Date of Birth",
    gender: "Gender",
    male: "Male",
    female: "Female",
    address: "Address (optional)",
    postcode: "Postcode (optional)",
    consent: "I agree to receive promotions and member updates from MZ Pro Trading via WhatsApp and email.",
    submit: "Register",
    required: "Required",
    invalid_name: "Please key in your name",
    invalid_phone: "Please key in a valid phone number",
    invalid_icnumber: "Please key in a valid IC / passport number",
    invalid_email: "Please key in a valid email",
    invalid_birthday: "Please choose your date of birth",
    invalid_consent: "Please tick the agreement to continue",
    exists_phone: "This phone number is already registered.",
    exists_icnumber: "This IC number is already registered.",
    exists: "You are already a member.",
    yourid: "Your Member ID",
    success: "Registration successful!",
    successnote: "Show this Member ID or your phone number at the counter when you purchase.",
    screenshot: "Tip: take a screenshot of this page.",
    error: "Something went wrong, please try again.",
  },
  zh: {
    title: "成为会员",
    subtitle: "免费注册，享受会员专属优惠与生日惊喜。",
    name: "全名（与身份证相同）",
    phone: "手机号码",
    icnumber: "身份证 / 护照号码",
    email: "电子邮件",
    birthday: "出生日期",
    gender: "性别",
    male: "男",
    female: "女",
    address: "地址（可选）",
    postcode: "邮编（可选）",
    consent: "我同意通过 WhatsApp 和电子邮件接收 MZ Pro Trading 的优惠和会员资讯。",
    submit: "注册",
    required: "必填",
    invalid_name: "请输入您的名字",
    invalid_phone: "请输入有效的手机号码",
    invalid_icnumber: "请输入有效的身份证 / 护照号码",
    invalid_email: "请输入有效的电子邮件",
    invalid_birthday: "请选择出生日期",
    invalid_consent: "请勾选同意条款以继续",
    exists_phone: "此手机号码已注册。",
    exists_icnumber: "此身份证号码已注册。",
    exists: "您已经是会员了。",
    yourid: "您的会员编号",
    success: "注册成功！",
    successnote: "购买时请在柜台出示此会员编号或手机号码。",
    screenshot: "提示：请截图保存此页面。",
    error: "出现错误，请再试一次。",
  },
  ms: {
    title: "Jadi Ahli",
    subtitle: "Daftar percuma dan nikmati promosi eksklusif ahli & hadiah hari jadi.",
    name: "Nama Penuh (seperti dalam IC)",
    phone: "Nombor Telefon",
    icnumber: "Nombor IC / Pasport",
    email: "E-mel",
    birthday: "Tarikh Lahir",
    gender: "Jantina",
    male: "Lelaki",
    female: "Perempuan",
    address: "Alamat (pilihan)",
    postcode: "Poskod (pilihan)",
    consent: "Saya bersetuju menerima promosi dan berita ahli daripada MZ Pro Trading melalui WhatsApp dan e-mel.",
    submit: "Daftar",
    required: "Wajib",
    invalid_name: "Sila masukkan nama anda",
    invalid_phone: "Sila masukkan nombor telefon yang sah",
    invalid_icnumber: "Sila masukkan nombor IC / pasport yang sah",
    invalid_email: "Sila masukkan e-mel yang sah",
    invalid_birthday: "Sila pilih tarikh lahir",
    invalid_consent: "Sila tandakan persetujuan untuk teruskan",
    exists_phone: "Nombor telefon ini sudah didaftarkan.",
    exists_icnumber: "Nombor IC ini sudah didaftarkan.",
    exists: "Anda sudah menjadi ahli.",
    yourid: "ID Ahli Anda",
    success: "Pendaftaran berjaya!",
    successnote: "Tunjukkan ID Ahli ini atau nombor telefon anda di kaunter semasa membeli.",
    screenshot: "Tip: tangkap layar halaman ini.",
    error: "Ralat berlaku, sila cuba lagi.",
  },
};

const today = new Date().toISOString().slice(0, 10);

// Malaysian IC: YYMMDD-PB-###G. Returns YYYY-MM-DD or ''.
const birthdayFromIc = (ic) => {
  const digits = ic.replace(/\D/g, '');
  if (digits.length !== 12) return '';
  const yy = parseInt(digits.slice(0, 2), 10);
  const mm = digits.slice(2, 4);
  const dd = digits.slice(4, 6);
  const currentYY = new Date().getFullYear() % 100;
  const year = (yy > currentYY ? 1900 : 2000) + yy;
  const date = `${year}-${mm}-${dd}`;
  const check = new Date(date + 'T00:00:00');
  if (isNaN(check.getTime()) || check.toISOString().slice(0, 10) > today) return '';
  return date;
};

class MemberRegister extends React.Component {
  state = {
    lang: (navigator.language || '').indexOf('zh') === 0 ? 'zh' : 'en',
    name: '',
    prefix: '60',
    phone: '',
    icnumber: '',
    email: '',
    birthday: '',
    gender: '',
    address: '',
    postcode: '',
    consent: false,
    errors: {},
    submitting: false,
    result: null, // { type: 'success' | 'exists', memberid, name, matched }
    servererror: '',
  };

  t = (key) => TEXT[this.state.lang][key];

  onIcChange = (value) => {
    const update = { icnumber: value };
    const derived = birthdayFromIc(value);
    if (derived && !this.state.birthday) update.birthday = derived;
    this.setState(update);
  };

  validate = () => {
    const s = this.state;
    const errors = {};
    const phoneDigits = s.phone.replace(/\D/g, '').replace(/^0/, '');
    if (!s.name.trim()) errors.name = 'invalid_name';
    if (phoneDigits.length < 7 || phoneDigits.length > 11) errors.phone = 'invalid_phone';
    if (s.icnumber.replace(/[\s-]/g, '').length < 6) errors.icnumber = 'invalid_icnumber';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email.trim())) errors.email = 'invalid_email';
    if (!s.birthday || s.birthday > today) errors.birthday = 'invalid_birthday';
    if (!s.consent) errors.consent = 'invalid_consent';
    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  submit = (e) => {
    e.preventDefault();
    if (this.state.submitting || !this.validate()) return;
    const s = this.state;
    const body = new URLSearchParams();
    body.append('name', s.name.trim());
    body.append('phone', s.prefix + s.phone.replace(/\D/g, '').replace(/^0/, ''));
    body.append('icnumber', s.icnumber);
    body.append('email', s.email.trim());
    body.append('birthday', s.birthday);
    body.append('gender', s.gender);
    body.append('address', s.address.trim());
    body.append('postcode', s.postcode.trim());
    body.append('consent', s.consent ? '1' : '0');
    body.append('language', s.lang);
    // Branch from the QR code link, e.g. /member/register?branch=Johor
    body.append('branch', new URLSearchParams(window.location.search).get('branch') || '');
    this.setState({ submitting: true, servererror: '' });
    fetch(MEMBER_API + 'mode=public-register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    })
      .then(r => r.json())
      .then(json => {
        if (json.success == 1) {
          this.setState({ submitting: false, result: { type: 'success', memberid: json.memberid, name: json.name } });
        } else if (json.exists == 1) {
          this.setState({ submitting: false, result: { type: 'exists', memberid: json.memberid, name: json.name, matched: json.matched } });
        } else if (json.field) {
          this.setState({ submitting: false, errors: { [json.field]: json.message } });
        } else {
          this.setState({ submitting: false, servererror: this.t('error') });
        }
      })
      .catch(() => this.setState({ submitting: false, servererror: this.t('error') }));
  };

  renderField(key, type, props = {}) {
    const error = this.state.errors[key];
    return (
      <FormGroup>
        <Label className="form-control-label" style={styles.label}>{this.t(key)}{props.optional ? '' : ' *'}</Label>
        <Input
          type={type}
          value={this.state[key]}
          invalid={!!error}
          onChange={e => this.setState({ [key]: e.target.value })}
          style={styles.input}
          {...props.input}
        />
        {error ? <div style={styles.error}>{this.t(error)}</div> : null}
      </FormGroup>
    );
  }

  renderResult() {
    const r = this.state.result;
    const isNew = r.type === 'success';
    return (
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        <div style={{ fontSize: 48 }}>{isNew ? '🎉' : '👋'}</div>
        <h2 style={{ margin: '8px 0 4px' }}>
          {isNew ? this.t('success') : (r.matched ? this.t('exists_' + r.matched) : this.t('exists'))}
        </h2>
        {!isNew ? <p style={{ color: '#525f7f' }}>{this.t('exists')}</p> : null}
        {r.memberid ? (
          <>
            {r.name ? <p style={{ fontSize: 18, marginBottom: 4 }}>{r.name}</p> : null}
            <div style={styles.idLabel}>{this.t('yourid')}</div>
            <div style={styles.idBox}>{r.memberid}</div>
          </>
        ) : null}
        <p style={{ color: '#525f7f', marginTop: 16 }}>{this.t('successnote')}</p>
        <p style={{ color: '#8898aa', fontSize: 13 }}>{this.t('screenshot')}</p>
      </div>
    );
  }

  render() {
    const s = this.state;
    return (
      <div style={styles.page}>
        <Helmet><title>MZ Pro Trading Membership</title></Helmet>
        <div style={styles.wrap}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <img src={require("assets/img/brand/mzprotradainglogo.png")} alt="MZ Pro Trading" style={{ maxWidth: 180, maxHeight: 80 }} />
          </div>
          <div style={styles.langRow}>
            {[['en', 'English'], ['zh', '中文'], ['ms', 'Bahasa Melayu']].map(([code, label]) => (
              <button key={code} type="button" onClick={() => this.setState({ lang: code })}
                style={Object.assign({}, styles.langBtn, s.lang === code ? styles.langBtnActive : {})}>{label}</button>
            ))}
          </div>
          <Card className="shadow border-0">
            <CardBody style={{ padding: '24px 20px' }}>
              {s.result ? this.renderResult() : (
                <Form onSubmit={this.submit} noValidate>
                  <h2 style={{ marginBottom: 4 }}>{this.t('title')}</h2>
                  <p style={{ color: '#525f7f', marginBottom: 20 }}>{this.t('subtitle')}</p>

                  {this.renderField('name', 'text', { input: { autoComplete: 'name', maxLength: 100 } })}

                  <FormGroup>
                    <Label className="form-control-label" style={styles.label}>{this.t('phone')} *</Label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Input type="select" value={s.prefix} onChange={e => this.setState({ prefix: e.target.value })} style={Object.assign({}, styles.input, { width: 96, flex: 'none' })}>
                        <option value="60">🇲🇾 +60</option>
                        <option value="65">🇸🇬 +65</option>
                      </Input>
                      <Input type="tel" inputMode="numeric" placeholder="12 345 6789" value={s.phone} invalid={!!s.errors.phone}
                        onChange={e => this.setState({ phone: e.target.value })} style={styles.input} autoComplete="tel-national" />
                    </div>
                    {s.errors.phone ? <div style={styles.error}>{this.t(s.errors.phone)}</div> : null}
                  </FormGroup>

                  <FormGroup>
                    <Label className="form-control-label" style={styles.label}>{this.t('icnumber')} *</Label>
                    <Input type="text" placeholder="900101-14-1234" value={s.icnumber} invalid={!!s.errors.icnumber}
                      onChange={e => this.onIcChange(e.target.value)} style={styles.input} maxLength={20} />
                    {s.errors.icnumber ? <div style={styles.error}>{this.t(s.errors.icnumber)}</div> : null}
                  </FormGroup>

                  {this.renderField('email', 'email', { input: { autoComplete: 'email', inputMode: 'email' } })}
                  {this.renderField('birthday', 'date', { input: { max: today } })}

                  <FormGroup>
                    <Label className="form-control-label" style={styles.label}>{this.t('gender')}</Label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {['Male', 'Female'].map(g => (
                        <button key={g} type="button" onClick={() => this.setState({ gender: g })}
                          style={Object.assign({}, styles.choice, s.gender === g ? styles.choiceActive : {})}>
                          {this.t(g.toLowerCase())}
                        </button>
                      ))}
                    </div>
                  </FormGroup>

                  {this.renderField('address', 'text', { optional: true, input: { autoComplete: 'street-address', maxLength: 255 } })}
                  {this.renderField('postcode', 'text', { optional: true, input: { autoComplete: 'postal-code', inputMode: 'numeric', maxLength: 10 } })}

                  <FormGroup check style={{ marginBottom: 20 }}>
                    <Label check style={{ fontSize: 14, color: '#525f7f' }}>
                      <Input type="checkbox" checked={s.consent} onChange={e => this.setState({ consent: e.target.checked })} />{' '}
                      {this.t('consent')}
                    </Label>
                    {s.errors.consent ? <div style={styles.error}>{this.t(s.errors.consent)}</div> : null}
                  </FormGroup>

                  {s.servererror ? <div style={Object.assign({}, styles.error, { marginBottom: 12 })}>{s.servererror}</div> : null}

                  <Button color="primary" type="submit" block size="lg" disabled={s.submitting}>
                    {s.submitting ? <Spinner size="sm" /> : this.t('submit')}
                  </Button>
                </Form>
              )}
            </CardBody>
          </Card>
          <p style={{ textAlign: 'center', color: '#8898aa', fontSize: 12, marginTop: 16 }}>© MZ Pro Trading</p>
        </div>
      </div>
    );
  }
}

const styles = {
  page: { minHeight: '100vh', background: 'linear-gradient(160deg, #172b4d 0%, #1a174d 45%, #f4f5f7 45.1%)', padding: '24px 16px' },
  wrap: { maxWidth: 480, margin: '0 auto' },
  langRow: { display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 16, flexWrap: 'wrap' },
  langBtn: { border: '1px solid rgba(255,255,255,0.5)', background: 'transparent', color: '#fff', borderRadius: 20, padding: '4px 14px', fontSize: 13, cursor: 'pointer' },
  langBtnActive: { background: '#fff', color: '#172b4d' },
  label: { fontSize: 14 },
  input: { fontSize: 16, color: '#32325d' },
  error: { color: '#f5365c', fontSize: 13, marginTop: 4 },
  choice: { flex: 1, border: '1px solid #cad1d7', background: '#fff', color: '#525f7f', borderRadius: 6, padding: '10px 0', fontSize: 15, cursor: 'pointer' },
  choiceActive: { borderColor: '#5e72e4', background: '#5e72e4', color: '#fff' },
  idLabel: { color: '#8898aa', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginTop: 12 },
  idBox: { display: 'inline-block', fontSize: 34, fontWeight: 700, letterSpacing: 3, color: '#172b4d', border: '2px dashed #5e72e4', borderRadius: 10, padding: '8px 20px', marginTop: 6 },
};

export default MemberRegister;
